// @owners { team: patients }
import { ShipmentFeeCohortMap } from '@alto/scriptdash/alto/orders/types/v1/shipment_fee_cohort';
import { type ShipmentFeeType } from '@alto/scriptdash/alto/orders/types/v1/shipment_fee_type';
import { ShipmentFeeTypeMap } from '@alto/scriptdash/alto/orders/types/v1/shipment_fee_type';
import {
  type FulfillmentOption,
  FulfillmentOptionTagMap,
  type FulfillmentWindowOption,
  type PickupData,
} from '@alto/scriptdash/alto/patient_app/checkout/v1/checkout_endpoint';
import { formatDollars } from '~shared/helpers/currency';
import { formatDate, formatDateStringForAPI } from '~shared/helpers/date';

const NON_DELIVERY_SHIPMENT_FEE_TYPES = new Set<ShipmentFeeType>(['on_demand_delivery', 'essentials_only_delivery']);

export const getFulfillmentOptionDeliveryFeeTypes = (
  fulfillmentOptions: (FulfillmentOption | null | undefined)[],
): ShipmentFeeType[] => {
  const feeTypes = new Set<ShipmentFeeType>();

  fulfillmentOptions.forEach((option) => {
    if (!option) return;

    option.available_fees?.forEach(({ fee_type }) => {
      if (!NON_DELIVERY_SHIPMENT_FEE_TYPES.has(fee_type)) {
        feeTypes.add(fee_type);
      }
    });

    const windowFeeType = option.fulfillment_window_option?.available_fee?.fee_type;
    if (windowFeeType && !NON_DELIVERY_SHIPMENT_FEE_TYPES.has(windowFeeType)) {
      feeTypes.add(windowFeeType);
    }
  });

  return [...feeTypes];
};

export const getFulfillmentDeliveryFeeTypeFlags = (fulfillmentOptions: (FulfillmentOption | null | undefined)[]) => {
  const feeTypes = getFulfillmentOptionDeliveryFeeTypes(fulfillmentOptions);

  return {
    includes_standard_delivery_fee: feeTypes.includes('standard_delivery'),
    includes_next_day_mail_fee: feeTypes.includes('next_day_mail'),
    includes_standard_mail_fee: feeTypes.includes('standard_mail'),
  };
};

export const buildTestDeliveryWindows = () => {
  return [
    {
      date: '2021-09-01',
      label: 'Express',
      description: 'by 11:45am (estimated)',
      delivery_window: {
        start_time: '2021-09-01T11:45:00Z',
        end_time: '2021-09-01T11:45:00Z',
        cutoff_time: '2021-09-01T11:45:00Z',
      },
      estimated_eta: '2021-09-01T11:45:00Z',
      available_fee: {
        fee_type: ShipmentFeeTypeMap.ASAP_DELIVERY,
        amount: 12.99,
        original_amount: 12.99,
        fee_cohort: ShipmentFeeCohortMap.ALTO,
        date: '2021-09-01',
      },
    },
    {
      date: '2021-09-01',
      label: 'Afternoon',
      description: '12:00 - 3:00pm',
      delivery_window: {
        start_time: '2021-09-01T12:00:00Z',
        end_time: '2021-09-01T15:00:00Z',
        cutoff_time: '2021-09-01T10:00:00Z',
      },
      estimated_eta: null,
      available_fee: {
        fee_type: ShipmentFeeTypeMap.SAME_DAY_DELIVERY,
        amount: 9.99,
        original_amount: 9.99,
        fee_cohort: ShipmentFeeCohortMap.ALTO,
        date: '2021-09-01',
      },
    },
    {
      date: '2021-09-01',
      label: 'Evening',
      description: '6:00 - 9:00pm',
      delivery_window: {
        start_time: '2021-09-01T18:00:00Z',
        end_time: '2021-09-01T21:00:00Z',
        cutoff_time: '2021-09-01T15:00:00Z',
      },
      estimated_eta: null,
      available_fee: {
        fee_type: ShipmentFeeTypeMap.SAME_DAY_DELIVERY,
        amount: 0,
        original_amount: 9.99,
        fee_cohort: ShipmentFeeCohortMap.ALTO,
        date: '2021-09-01',
      },
    },
  ];
};

export const createCustomFulfillmentOption = (deliveryDate: string, deliveryWindow?: FulfillmentWindowOption) => {
  const availableFee = deliveryWindow?.available_fee;
  const { amount: feeAmount, original_amount: feeOriginalAmount } = availableFee ?? { amount: 0, original_amount: 0 };

  return {
    label: formatDate(deliveryDate),
    date: formatDateStringForAPI(deliveryDate),
    fee_amount_label: feeAmount > 0 ? formatDollars(feeAmount || 0) : 'Free',
    fee_original_amount_label: feeAmount > 0 ? formatDollars(feeOriginalAmount || 0) : 'Free',
    tag: null,
    available_fees: availableFee ? [availableFee] : [],
    pickup_data: null,
    // We set fulfillment_window_option here to null because when selecting a custom date, we dont know at time
    // of date selection if there is only a single window. Because of this we set this as nil as it's only used
    // if the date has a single delivery window available.
    fulfillment_window_option: null,
  } as FulfillmentOption;
};

export const getTimeOfDay = (time: string) => {
  // Split the time string into hours and the rest
  const [hours, period] = time.split(':');

  // Convert the hours to a number
  const numericHours = Number(hours);

  // Determine the time of day based on the hours and period
  if (period.includes('AM') && numericHours < 12) {
    return 'Morning';
  } else if (period.includes('PM') && (numericHours === 12 || (numericHours >= 1 && numericHours < 5))) {
    return 'Afternoon';
  } else {
    return 'Evening';
  }
};

export const initSelectedFulfillmentWindowOption = ({
  orderDate,
  orderDeliverAfter,
  orderDeliverBefore,
  isAsap = false,
}: {
  orderDate: string | undefined;
  orderDeliverAfter: string | undefined;
  orderDeliverBefore: string | undefined;
  isAsap?: boolean;
}): FulfillmentWindowOption | undefined => {
  if (orderDate && orderDeliverAfter && orderDeliverBefore) {
    return {
      available_fee: undefined,
      date: orderDate,
      estimated_eta: undefined,
      label: isAsap ? 'Express' : getTimeOfDay(orderDeliverAfter),
      description: isAsap ? `by ${orderDeliverBefore} (estimated)` : `${orderDeliverAfter} - ${orderDeliverBefore}`,
      delivery_window: {
        start_time: orderDeliverAfter,
        end_time: orderDeliverBefore,
        cutoff_time: '',
      },
    };
  }
  return undefined;
};

// Returns a sorted by date (ascending) list of radio date options. If a customFulfillmentOption is provided,
// it will be added to the list. For the second order in a split shipment, only the selected date is shown
// (date cannot be changed) because only one date can be sent when posting to /v3/deliveries.
export const getRadioDateOptions = ({
  courierDateFulfillmentOptions,
  mailFulfillmentOptions,
  customFulfillmentOption,
  isSecondSplitShipment,
  selectedDate,
  isMailOrder,
}: {
  courierDateFulfillmentOptions: FulfillmentOption[];
  mailFulfillmentOptions?: FulfillmentOption[];
  customFulfillmentOption?: FulfillmentOption | null;
  isSecondSplitShipment?: boolean;
  selectedDate?: string | null;
  isMailOrder?: boolean;
}) => {
  const baseOptions = isMailOrder ? (mailFulfillmentOptions ?? []) : courierDateFulfillmentOptions;
  const radioDateOptions = customFulfillmentOption ? [...baseOptions, customFulfillmentOption] : baseOptions;

  // For second of split shipment: only show the selected date; patient cannot change it.
  if (isSecondSplitShipment && selectedDate) {
    const filtered = radioDateOptions.filter((option) => option.date === selectedDate);
    if (filtered.length > 0) return filtered;
  }

  return [...radioDateOptions].sort((a, b) => {
    if (a.date && b.date) {
      return a.date.localeCompare(b.date);
    }
    return 0;
  });
};

export const formatStoreTime = (time: string) => {
  const originalHour = new Date(time).getUTCHours();
  const originalMinutes = new Date(time).getUTCMinutes();

  const hour = originalHour > 12 ? originalHour % 12 : originalHour;
  const minutes = originalMinutes < 10 ? `0${originalMinutes}` : originalMinutes;
  const amPm = originalHour >= 12 ? 'pm' : 'am';

  if (minutes === '00') {
    return `${hour}${amPm}`;
  }

  return `${hour}:${minutes}${amPm}`;
};

export const getDateKey = ({
  date,
  isPickup,
  tag,
}: {
  date: string | null | undefined;
  isPickup: boolean;
  tag: 'BUNDLED' | null | undefined;
}) => {
  if (isPickup) return `PICKUP-${date}`;

  return `${date}${tag ? '-' + tag : ''}`;
};

/** Radio group value derived from Redux order date and checkout flow state. */
export const getCheckedValueFromOrder = ({
  orderDate,
  isOrderPickupMethod,
  isOrderBundling,
}: {
  orderDate: string | undefined | null;
  isOrderPickupMethod: boolean;
  isOrderBundling: boolean;
}): string | null => {
  if (!orderDate) return null;

  if (isOrderPickupMethod) {
    return getDateKey({ date: orderDate, isPickup: true, tag: undefined });
  }

  if (isOrderBundling) {
    return getDateKey({ date: orderDate, isPickup: false, tag: FulfillmentOptionTagMap.BUNDLED });
  }

  return orderDate;
};

const fulfillmentWindowMatchesOrder = ({
  window,
  orderDeliverAfter,
  orderDeliverBefore,
}: {
  window: FulfillmentWindowOption | null | undefined;
  orderDeliverAfter: string;
  orderDeliverBefore: string;
}) => {
  const deliveryWindow = window?.delivery_window;
  return deliveryWindow?.start_time === orderDeliverAfter && deliveryWindow?.end_time === orderDeliverBefore;
};

/** Resolves the selected window from fulfillment options when possible; otherwise reconstructs from Redux. */
export const resolveSelectedFulfillmentWindowOption = ({
  orderDate,
  orderDeliverAfter,
  orderDeliverBefore,
  isAsap,
  fulfillmentOptions,
}: {
  orderDate: string | undefined | null;
  orderDeliverAfter: string | undefined;
  orderDeliverBefore: string | undefined;
  isAsap?: boolean;
  fulfillmentOptions: FulfillmentOption[];
}): FulfillmentWindowOption | undefined => {
  if (!orderDate || !orderDeliverAfter || !orderDeliverBefore) return undefined;

  for (const option of fulfillmentOptions) {
    if (option.date !== orderDate) continue;

    const window = option.fulfillment_window_option;
    if (fulfillmentWindowMatchesOrder({ window, orderDeliverAfter, orderDeliverBefore })) {
      return window ?? undefined;
    }
  }

  return initSelectedFulfillmentWindowOption({
    orderDate,
    orderDeliverAfter,
    orderDeliverBefore,
    isAsap,
  });
};

/** Builds a custom fulfillment option when the order date is not in the standard option list. */
export const deriveCustomFulfillmentOption = ({
  orderDate,
  baseOptions,
  anyDateOption,
  selectedFulfillmentWindowOption,
}: {
  orderDate: string;
  baseOptions: FulfillmentOption[];
  anyDateOption: FulfillmentOption | null | undefined;
  selectedFulfillmentWindowOption: FulfillmentWindowOption | undefined;
}): FulfillmentOption | undefined => {
  if (baseOptions.some((option) => option?.date === orderDate)) return undefined;

  const { available_fees, fee_amount_label, fee_original_amount_label } = anyDateOption || {};
  const feeOverrides = {
    available_fees: available_fees ?? [],
    fee_amount_label,
    fee_original_amount_label,
  };

  if (selectedFulfillmentWindowOption) {
    return {
      ...createCustomFulfillmentOption(orderDate, selectedFulfillmentWindowOption),
      ...feeOverrides,
    };
  }

  return {
    ...createCustomFulfillmentOption(orderDate),
    ...feeOverrides,
  };
};

export const getFeeLabels = ({
  fulfillmentOption,
  selectedWindowOption,
  isDateOptionSelected,
}: {
  fulfillmentOption: FulfillmentOption;
  selectedWindowOption?: FulfillmentWindowOption;
  isDateOptionSelected: boolean;
}) => {
  const feeAmountLabel = fulfillmentOption.fee_amount_label ?? undefined;
  const feeOriginalAmountLabel = fulfillmentOption.fee_original_amount_label ?? undefined;
  if (!isDateOptionSelected || !selectedWindowOption) {
    return {
      feeAmountLabel,
      feeOriginalAmountLabel,
    };
  }

  const selectedAmount = selectedWindowOption.available_fee?.amount;
  const selectedOriginalAmount = selectedWindowOption.available_fee?.original_amount;

  let selectedFeeAmountLabel: string | undefined;
  if (selectedAmount === 0 && selectedOriginalAmount) {
    selectedFeeAmountLabel = 'Free';
  } else if (!!selectedAmount && selectedAmount > 0) {
    selectedFeeAmountLabel = formatDollars(selectedAmount);
  } else {
    selectedFeeAmountLabel = feeAmountLabel;
  }

  const selectedFeeOriginalAmountLabel = selectedOriginalAmount
    ? formatDollars(selectedOriginalAmount)
    : feeOriginalAmountLabel;

  return { feeAmountLabel: selectedFeeAmountLabel, feeOriginalAmountLabel: selectedFeeOriginalAmountLabel };
};

export const getStoreHours = (pickupData: PickupData) => {
  const {
    facility_weekday_store_open,
    facility_weekday_store_close,
    facility_weekend_store_open,
    facility_weekend_store_close,
  } = pickupData;
  const today = new Date().getDay();
  const storeOpenTime = today === 0 || today === 6 ? facility_weekend_store_open : facility_weekday_store_open;
  const storeCloseTime = today === 0 || today === 6 ? facility_weekend_store_close : facility_weekday_store_close;
  return { storeOpenTime, storeCloseTime };
};
